import { Alert, AlertIcon, HStack } from "@chakra-ui/react";
import { AddButton, Card, NavBar } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { ExpenseTypeEnum } from "@src/__generated__/urql-graphql";
import { observer } from "mobx-react-lite";
import { Fragment, useRef, useState } from "react";
import { useDebounce } from "react-use";
import { useStore } from "../../store";
import { ContractorFiles } from "./ContractorFiles";
import UploadFile from "./UploadFile";

export const FilesCard = observer(function FilesCard() {
  const store = useStore();
  const setTab = (newTabIndex: 0 | 1) => {
    store.fileCardActiveTab = newTabIndex;
  };
  const toggleTab = () => {
    store.fileCardActiveTab = store.fileCardActiveTab === 0 ? 1 : 0;
  };

  const [showMaxFilesError, setMaxFilesError] = useState(false);
  const hideMaxFilesError = () => setMaxFilesError(false);
  useDebounce(hideMaxFilesError, 3000, [showMaxFilesError]);

  const file1 = store.form.files[0].file;
  const file2 = store.form.files[1].file;

  const handleGetPrediction = async (file: File, tabIndex?: 0 | 1) => {
    if (store.predictionBudgetItemsCheck || store.predictionFormCheck) {
      await store.getPrediction(file);
    } else {
      store.predictionFiles[tabIndex ?? store.fileCardActiveTab] = file;
      store.predictionFilledForm[tabIndex ?? store.fileCardActiveTab] = false;
      store.predictionFilledBudgetItems[tabIndex ?? store.fileCardActiveTab] =
        false;
    }

    if (store.predictionFormCheck) {
      store.predictionFillForm();
    }

    if (store.predictionBudgetItemsCheck) {
      store.predictionFillBudgetItems();
    }
  };

  const handleDropFiles = (files: File[]) => {
    if (files[0]) {
      handleGetPrediction(files[0]);

      store.form.files[store.fileCardActiveTab].fileToUpload.onChange(files[0]);
    }

    if (files[1]) {
      const reverseIndex = store.fileCardActiveTab === 0 ? 1 : 0;
      handleGetPrediction(files[1], reverseIndex);

      store.form.files[reverseIndex].fileToUpload.onChange(files[1]);
    }

    if (files.length > 2) {
      setMaxFilesError(true);
    }
  };

  const handleEmptyTab = () => {
    store.removePredictionFile();
    if (!store.form.files[store.fileCardActiveTab].file) {
      toggleTab();
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Card
      sectioned
      w="full"
      pos={{
        base: "relative",
        md: "sticky",
      }}
      top={{ base: "unset", md: "4" }}
    >
      {showMaxFilesError && (
        <div className="mb-2">
          <Alert status="warning">
            <AlertIcon />
            <Trans>You can upload max. 2 files</Trans>
          </Alert>
        </div>
      )}

      {store.form.type.value === ExpenseTypeEnum.Contract ? (
        <ContractorFiles />
      ) : (
        <Fragment>
          <HStack justifyContent="space-between">
            <NavBar size={null} className="h-12 mb-5">
              {file1 && (
                <NavBar.Item
                  underline={false}
                  active={store.fileCardActiveTab === 0}
                  onClick={() => setTab(0)}
                >
                  {file1.fileName}
                </NavBar.Item>
              )}

              {file2 && (
                <NavBar.Item
                  underline={false}
                  active={store.fileCardActiveTab === 1}
                  onClick={() => setTab(1)}
                >
                  {file2.fileName}
                </NavBar.Item>
              )}
            </NavBar>
            {(file1 ? !file2 : !!file2) && (
              <AddButton
                iconType="normal"
                iconSize="small"
                onClick={() => {
                  toggleTab();
                  fileInputRef.current?.click();
                }}
              >
                {t`Add file`}
              </AddButton>
            )}
          </HStack>

          <UploadFile
            ref={fileInputRef}
            value={store.form.files[store.fileCardActiveTab]}
            onDropFiles={handleDropFiles}
            onRemoveFile={handleEmptyTab}
          />
        </Fragment>
      )}
    </Card>
  );
});
